import { useMemo } from 'react'
import { collection, doc, query, serverTimestamp, where } from 'firebase/firestore'
import type { Query } from 'firebase/firestore'
import { fbt } from 'fbtee'
import { db } from '../lib/firebase'
import { commitWrites, updateDoc, type BatchWrite } from '../lib/firestore'
import { runMutation } from '../lib/firestoreMutation'
import { track } from '../lib/analytics'
import { useFirestoreCollection } from './useFirestore'
import type { GroupEventRequest } from '../types'

/** A group's pending event suggestions, oldest first. Pass null (non-members) to skip the listener. */
export function useGroupPendingRequests(groupId: string | null) {
  const ref = useMemo(
    () =>
      groupId
        ? (query(
            collection(db, 'groupEventRequests'),
            where('groupId', '==', groupId),
            where('status', '==', 'pending'),
          ) as Query<GroupEventRequest>)
        : null,
    [groupId],
  )
  const { data, loading, error } = useFirestoreCollection<GroupEventRequest>(ref)
  const requests = useMemo(() => [...data].sort(byCreatedAt), [data])
  return { requests, loading, error }
}

export const byCreatedAt = (a: GroupEventRequest, b: GroupEventRequest) =>
  (a.createdAt?.toMillis() ?? 0) - (b.createdAt?.toMillis() ?? 0)

/**
 * Approve a suggestion: one batch links the event (unless it's linked already)
 * and marks the request reviewed, so neither can land without the other.
 */
export function approveGroupEventRequest(req: GroupEventRequest, userId: string, alreadyLinked: boolean) {
  return runMutation(fbt("Couldn't approve suggestion", 'Error toast when approving an event suggested to a group fails'), async () => {
    const writes: BatchWrite[] = [{
      kind: 'update',
      ref: doc(db, 'groupEventRequests', req.id),
      data: { status: 'approved', reviewedBy: userId, reviewedAt: serverTimestamp() },
    }]
    if (!alreadyLinked) {
      writes.push({
        kind: 'set',
        ref: doc(collection(db, 'groupEvents')),
        data: { groupId: req.groupId, eventId: req.eventId, createdBy: userId, createdAt: serverTimestamp() },
      })
    }
    await commitWrites(db, writes)
    track('group_event_linked', { group_id: req.groupId, event_id: req.eventId, entry_point: 'request_approval' })
  })
}

export function rejectGroupEventRequest(req: GroupEventRequest, userId: string) {
  return runMutation(fbt("Couldn't decline suggestion", 'Error toast when declining an event suggested to a group fails'), async () => {
    await updateDoc(doc(db, 'groupEventRequests', req.id), {
      status: 'rejected', reviewedBy: userId, reviewedAt: serverTimestamp(),
    })
    track('group_event_request_rejected', { group_id: req.groupId, event_id: req.eventId })
  })
}
